import { Badge, Stack } from "@mui/material";
import { RoomContext } from "App/context";
import GenshinButton from "common/components/GenshinButton";
import SkewButton from "common/components/SkewButton";
import BoxListWithContext from "pages/Draft/BoxList";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DraftCharacter } from "types/Draft/type";
import { Role, Room } from "types/Room/type";
import { useSocket } from "common/hooks/useSocket";

export type BanSelectionProps = {
  badgeQuantity?: number;
  whoBan?: "player1" | "player2";
  showEvent: string;
  clickEvent: string;
  selectedEvent: string;
  sendEvent: string;
  buttonText: string;
  dispatch: (action: Partial<Record<string, unknown>>) => void;
  selectingBans: Role | undefined;
  selectedBansPlayer1?: DraftCharacter[];
  selectedBansPlayer2?: DraftCharacter[];
  roomUpdate: (room: Room, dispatchRoom: (room: Partial<Room>) => void) => void;
  selectedUpdate: (
    chars: DraftCharacter[],
    role: Role,
    dispatch: (action: Partial<Record<string, unknown>>) => void,
  ) => void;
  // Solo : camp au nom duquel le joueur pose les bans.
  soloSide?: "Player 1" | "Player 2";
};

const BanSelection = (props: BanSelectionProps) => {
  const {
    badgeQuantity = 0,
    whoBan = "player1",
    showEvent,
    clickEvent,
    selectedEvent,
    sendEvent,
    buttonText,
    dispatch,
    selectingBans,
    selectedBansPlayer1,
    selectedBansPlayer2,
    roomUpdate,
    selectedUpdate,
    soloSide,
  } = props;
  const { t } = useTranslation();
  const {
    dispatch: dispatchRoom,
    role,
    player1,
    player2,
    params,
  } = useContext(RoomContext);
  const socket = useSocket();
  const isSoloCaptain =
    params.solo === true && (role === "Player 1" || role === "Player 2");
  /*
    En solo, le camp au nom duquel on bannit est choisi une seule fois pour les deux familles
    (voir SoloBanSide) : un même camp reçoit bans d'équilibrage et bans joker. Pendant la
    sélection, c'est le camp de la sélection en cours qui fait foi.
  */
  const banRole: Role = isSoloCaptain
    ? (selectingBans ?? soloSide ?? (whoBan === "player1" ? "Player 1" : "Player 2"))
    : role;

  useEffect(() => {
    socket.on(
      showEvent,
      (
        room: Room,
        selectedBans?: DraftCharacter[],
        role?: Extract<Role, "Player 1" | "Player 2">,
      ) => {
        roomUpdate(room, dispatchRoom);
        dispatch({
          selectingBans: role ?? undefined,
          selectedBansPlayer1:
            role === "Player 1" ? (selectedBans ?? []) : undefined,
          selectedBansPlayer2:
            role === "Player 2" ? (selectedBans ?? []) : undefined,
        });
      },
    );
    socket.on(selectedEvent, (chars: DraftCharacter[], role: Role) => {
      selectedUpdate(chars, role, dispatch);
    });

    return () => {
      socket.removeListener(showEvent);
      socket.removeListener(selectedEvent);
    };
  }, []);

  return (
    <Stack
      alignItems="center"
      direction="column"
      height="100%"
      gap={2}
      width={selectingBans !== undefined ? "100%" : undefined}
    >
      <Badge
        color={whoBan === "player1" ? "info" : "error"}
        badgeContent={Math.abs(badgeQuantity)}
        anchorOrigin={{
          vertical: "top",
          horizontal: whoBan === "player1" ? "left" : "right",
        }}
      >
        <GenshinButton
          disabled={
            // Seuls les capitaines négocient : les sièges 5 à 8 passaient entre les mailles.
            (role !== "Player 1" && role !== "Player 2") ||
            player1 === undefined ||
            player2 === undefined ||
            (selectingBans !== undefined && selectingBans !== banRole)
          }
          variant="contained"
          onClick={() => {
            if (role !== "Spectator") {
              if (selectingBans !== undefined) {
                socket.emit(clickEvent);
              } else {
                socket.emit(clickEvent, banRole);
              }
            }
          }}
        >
          {buttonText}
        </GenshinButton>
      </Badge>
      {selectingBans !== undefined && (
        <Stack
          direction="column"
          alignItems="center"
          height="0px"
          flexGrow={1}
          width="100%"
          gap={1}
        >
          <BoxListWithContext />
          {selectingBans === banRole && (
            <SkewButton
              onClick={() =>
                socket.emit(
                  sendEvent,
                  selectedBansPlayer1 ?? selectedBansPlayer2,
                  banRole,
                )
              }
            >
              {t("Bannir")}
            </SkewButton>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default BanSelection;
